import { Logger } from '../../utils/logger';

export type AtlasInteractionNetworkEndpoint =
  | 'succession_projection'
  | 'succession_save'
  | 'scenario_review'
  | 'scenario_apply'
  | 'operational_prepare'
  | 'operational_confirm'
  | 'operational_status';

const SAFE_SERVER_TIMING_NAMES = new Set([
  'auth_jwt',
  'auth_user_hydration',
  'auth_total',
  'dependency_setup',
  'account_context',
  'feature_flags',
  'entitlement_target',
  'entitlement_resolution',
  'pre_controller',
  'scope_authorization',
  'protected_authorization',
  'plan_and_review',
  'transaction_authorization',
  'canonical_commit',
  'projection',
  'plan_load',
  'simulation',
  'persistence',
  'freshness',
  'gate',
  'convergence',
  'access_summary',
  'succession_projection',
  'succession_persistence',
  'db_queries',
  'db_observed_waves',
  'request_total',
  'total',
]);

export async function observeAtlasInteractionRequest(
  url: string,
  options: RequestInit,
  request: () => Promise<Response>,
): Promise<Response> {
  const endpoint = atlasInteractionNetworkEndpoint(url, options.method);
  if (!endpoint) return request();
  const startedAt = performance.now();
  try {
    const response = await request();
    logTiming({
      endpoint,
      status: response.status,
      outcome: response.ok ? 'ok' : 'http_error',
      browserTotalMs: durationMs(performance.now() - startedAt),
      serverTiming: safeServerTiming(response.headers.get('server-timing')),
    });
    return response;
  } catch (error) {
    logTiming({
      endpoint,
      status: null,
      outcome: error instanceof DOMException && error.name === 'AbortError'
        ? 'aborted'
        : 'network_error',
      browserTotalMs: durationMs(performance.now() - startedAt),
      serverTiming: [],
    });
    throw error;
  }
}

export function atlasInteractionNetworkEndpoint(
  url: string,
  method = 'GET',
): AtlasInteractionNetworkEndpoint | null {
  const path = url.split('?', 1)[0] ?? '';
  const normalizedMethod = method.toUpperCase();
  if (normalizedMethod === 'GET'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/succession\/?$/.test(path)) {
    return 'succession_projection';
  }
  if (normalizedMethod === 'PUT'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/succession\/change-set\/?$/.test(path)) {
    return 'succession_save';
  }
  if (normalizedMethod === 'GET'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/organization-plans\/[^/]+\/review\/?$/.test(path)) {
    return 'scenario_review';
  }
  if (normalizedMethod === 'POST'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/organization-plans\/[^/]+\/apply\/?$/.test(path)) {
    return 'scenario_apply';
  }
  if (normalizedMethod === 'POST'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/organization-plans\/[^/]+\/prepare-operational\/?$/.test(path)) {
    return 'operational_prepare';
  }
  if (normalizedMethod === 'POST'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/organization-changes\/[^/]+\/apply\/?$/.test(path)) {
    return 'operational_confirm';
  }
  if (normalizedMethod === 'GET'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/organization-changes\/[^/]+\/status\/?$/.test(path)) {
    return 'operational_status';
  }
  if (normalizedMethod === 'POST'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/operational-changes\/[^/]+\/confirm\/?$/.test(path)) {
    return 'operational_confirm';
  }
  if (normalizedMethod === 'GET'
    && /^\/api\/folders\/[^/]+\/diagrams\/[^/]+\/atlas\/operational-changes\/[^/]+\/status\/?$/.test(path)) {
    return 'operational_status';
  }
  return null;
}

export interface SafeServerTimingMetric {
  readonly name: string;
  readonly durationMs?: number;
  readonly count?: number;
}

export function safeServerTiming(header: string | null): SafeServerTimingMetric[] {
  if (!header) return [];
  const metrics: SafeServerTimingMetric[] = [];
  for (const segment of header.split(',')) {
    const [rawName, ...parameters] = segment.trim().split(';');
    const name = rawName?.trim();
    if (!name || !SAFE_SERVER_TIMING_NAMES.has(name)) continue;
    const duration = numberParameter(parameters, 'dur');
    const description = stringParameter(parameters, 'desc');
    metrics.push({
      name,
      ...(duration !== null ? { durationMs: duration } : {}),
      ...(description !== null && /^\d+$/.test(description)
        ? { count: Number(description) }
        : {}),
    });
  }
  return metrics;
}

function numberParameter(parameters: readonly string[], name: string): number | null {
  const raw = parameters.find((parameter) => parameter.trim().startsWith(`${name}=`));
  if (!raw) return null;
  const value = Number(raw.slice(raw.indexOf('=') + 1).replace(/^"|"$/g, ''));
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function stringParameter(parameters: readonly string[], name: string): string | null {
  const raw = parameters.find((parameter) => parameter.trim().startsWith(`${name}=`));
  return raw ? raw.slice(raw.indexOf('=') + 1).trim().replace(/^"|"$/g, '') : null;
}

function logTiming(input: {
  endpoint: AtlasInteractionNetworkEndpoint;
  status: number | null;
  outcome: 'ok' | 'http_error' | 'aborted' | 'network_error';
  browserTotalMs: number;
  serverTiming: readonly SafeServerTimingMetric[];
}): void {
  Logger.info('AtlasInteractionNetworkTiming', 'Atlas request completed', JSON.stringify(input));
}

function durationMs(value: number): number {
  return Math.max(0, Math.round(value * 10) / 10);
}
